import React, {
  useRef,
  useState,
  forwardRef,
  useEffect,
  ReactNode,
  RefObject,
} from 'react';
import { IconAngleLeft, IconAngleRight, IconPenFilled } from '@iwoca/icons';
import { Button, Select } from '@iwoca/orion';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import clsx from 'clsx';
import { de } from 'date-fns/locale';
import { useLocale } from 'hooks/useLocale';
import DatePicker, {
  CalendarContainer,
  registerLocale,
} from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { TTime } from 'types';
import { DateFieldInput } from './DateFieldInput';
import { DateInput } from './DateInput';

import styles from './DateTimePicker.module.css';

type TDateIntervals = { start: Date; end: Date }[] | undefined;

type TProps = {
  timeOptions?: TTime[];
  showTimeInput?: boolean;
  className?: string;
  includeDateIntervals?: TDateIntervals;
  disableTimeOption?: (id: string, selectedDate: Date | null) => boolean;
  handleChange: (
    contactDate: Date | null,
    contactTime?: string | undefined,
  ) => void;
  filterDate?: (date: Date) => boolean;
  withInput?: boolean;
  // Render the trigger as a labelled field matching sibling Input fields.
  fieldInput?: boolean;
  label?: ReactNode;
  error?: ReactNode;
  disabled?: boolean;
  defaultDate?: string;
  dateInputRef?: RefObject<any>;
};

export const DateTimePicker = ({
  timeOptions,
  showTimeInput = false,
  className,
  includeDateIntervals,
  disableTimeOption,
  filterDate,
  handleChange,
  withInput,
  fieldInput,
  label,
  error,
  disabled,
  defaultDate,
  dateInputRef,
}: TProps) => {
  const { i18n, isDE } = useLocale();
  const inputRef = useRef<InstanceType<typeof DatePicker> | null>(null);
  const todaysDate = new Date();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | null>(
    null,
  );
  const [selectedTime, setSelectedTime] = useState<string | undefined>('');
  const [timeInputValue, setTimeInputValue] = useState<string | undefined>('');

  registerLocale('de', de);

  const setDate = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
      setTimeInputValue('');
    }
  };

  const StyledCalendarContainer = ({
    children,
  }: {
    children: React.ReactNode;
  }) => {
    return (
      <CalendarContainer
        data-testid="calendar"
        className={styles.calendarContainer}
      >
        {children}
      </CalendarContainer>
    );
  };

  const fieldDisplayValue = defaultDate
    ? i18n.date(new Date(defaultDate), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '';

  const longDate = (date: Date) => {
    return i18n.date(date, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const YEARS = Array.from({ length: 2200 - 2011 + 1 }, (_, i) => 2011 + i);
  const MONTHS = [
    { label: msg`January`, value: 'January' },
    { label: msg`February`, value: 'February' },
    { label: msg`March`, value: 'March' },
    { label: msg`April`, value: 'April' },
    { label: msg`May`, value: 'May' },
    { label: msg`June`, value: 'June' },
    { label: msg`July`, value: 'July' },
    { label: msg`August`, value: 'August' },
    { label: msg`September`, value: 'September' },
    { label: msg`October`, value: 'October' },
    { label: msg`November`, value: 'November' },
    { label: msg`December`, value: 'December' },
  ];

  const toggleCalendar = (open: boolean) => {
    if (inputRef.current !== null) {
      inputRef.current.setOpen(open);
    }
  };

  const ContactTimeButton = forwardRef<HTMLButtonElement>((_props, ref) => {
    const dateToDisplay = selectedDate
      ? i18n.date(selectedDate, {
          day: '2-digit',
          month: 'short',
        })
      : null;

    const timeLabelIndexAfterSelected =
      timeOptions && selectedTime
        ? timeOptions.findIndex((x) => x.id === selectedTime)
        : 0;

    const timeToDisplay =
      timeOptions && i18n._(timeOptions[timeLabelIndexAfterSelected].label);

    return (
      <button
        type="button"
        data-testid="custom-input"
        className={styles.customInput}
        onClick={() => toggleCalendar(true)}
        ref={ref}
      >
        {selectedTime && selectedTime !== 'asap' ? (
          <Trans>
            {dateToDisplay} from {timeToDisplay}
          </Trans>
        ) : (
          <Trans>As soon as possible</Trans>
        )}
        <IconPenFilled className={styles.inputIcon} />
      </button>
    );
  });

  const handleClose = () => {
    setSelectedDate(selectedCalendarDate);
    setTimeInputValue(selectedTime);
  };

  ContactTimeButton.displayName = 'ContactTimeButton';

  useEffect(() => {
    setSelectedDate(defaultDate ? new Date(defaultDate) : null);
  }, [defaultDate]);

  return (
    <DatePicker
      withPortal
      portalId="datepicker-portal"
      locale={isDE ? de : undefined}
      data-testid="date-picker-portal"
      disabledKeyboardNavigation
      disabled={disabled}
      ref={inputRef}
      customInput={
        fieldInput ? (
          <DateFieldInput
            label={label}
            error={error}
            displayValue={fieldDisplayValue}
            disabled={disabled}
          />
        ) : withInput ? (
          <DateInput
            toggleCalendar={toggleCalendar}
            handleChange={handleChange}
            defaultDate={defaultDate}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            calendarDate={selectedCalendarDate}
            filterDate={filterDate}
            dateInputRef={dateInputRef}
          />
        ) : (
          <ContactTimeButton />
        )
      }
      className={clsx(className, styles.dateTimePicker)}
      selected={selectedDate}
      timeInputLabel=""
      timeFormat="HH:mm"
      onChange={setDate}
      filterDate={filterDate}
      includeDateIntervals={includeDateIntervals ?? undefined}
      calendarStartDay={1}
      dayClassName={(date) => {
        return clsx(styles.day, {
          [styles.todaysDate]: longDate(date) === longDate(todaysDate),
          [styles.selectedDate]:
            selectedDate && longDate(date) === longDate(selectedDate),
          [styles.disabledDate]: !filterDate?.(date),
        });
      }}
      weekDayClassName={() => styles.weekDay}
      shouldCloseOnSelect={false}
      dateFormat={showTimeInput ? "EE, do MMM 'from' h:mm aa" : 'EE, do MMM'}
      calendarContainer={StyledCalendarContainer}
      onClickOutside={handleClose}
      renderCustomHeader={({
        monthDate,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
        changeMonth,
        changeYear,
      }) => {
        const currentMonth = monthDate.getMonth();
        const currentYear = monthDate.getFullYear();

        return (
          <div className={styles.monthHeader}>
            <button
              type="button"
              data-testid="data-picker-previous-month"
              className={styles.monthButton}
              onClick={decreaseMonth}
              disabled={nextMonthButtonDisabled}
            >
              <IconAngleLeft />
            </button>

            <div className={styles.dateSelector}>
              <Select
                value={MONTHS[currentMonth].value}
                onChange={(e) =>
                  changeMonth(
                    MONTHS.findIndex((month) => e.target.value === month.value),
                  )
                }
                wrapperClassName={styles.monthSelector}
                data-testid="month-selector"
              >
                {MONTHS.map((month) => (
                  <option key={month.value} value={month.value}>
                    {i18n._(month.label)}
                  </option>
                ))}
              </Select>

              <Select
                value={currentYear}
                onChange={(e) => changeYear(Number(e.target.value))}
                wrapperClassName={styles.yearSelector}
                data-testid="year-selector"
              >
                {YEARS.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Select>
            </div>

            <button
              type="button"
              data-testid="data-picker-next-month"
              className={styles.monthButton}
              onClick={increaseMonth}
              disabled={prevMonthButtonDisabled}
            >
              <IconAngleRight />
            </button>
          </div>
        );
      }}
    >
      {showTimeInput && timeOptions && (
        <div className={styles.timeInputContainer}>
          <Select
            data-testid="time-input"
            onChange={(e) => {
              setTimeInputValue(e.target.value);
            }}
            value={timeInputValue}
          >
            <option disabled value="">
              <Trans>Choose a time</Trans>
            </option>
            {timeOptions.map((time) => {
              return (
                <option
                  key={`${time.id}`}
                  value={time.id}
                  data-testid={time.id}
                  disabled={
                    disableTimeOption
                      ? disableTimeOption(time.id, selectedDate)
                      : false
                  }
                >
                  {i18n._(time.label)}
                </option>
              );
            })}
          </Select>
        </div>
      )}
      <div className={styles.buttonsContainer}>
        <Button
          type="button"
          variant="tertiary"
          className={styles.button}
          onClick={() => {
            handleClose();
            toggleCalendar(false);
          }}
        >
          <Trans>Cancel</Trans>
        </Button>
        <Button
          type="button"
          variant="primary"
          className={styles.button}
          disabled={
            showTimeInput ? !selectedDate || !timeInputValue : !selectedDate
          }
          onClick={() => {
            setSelectedTime(timeInputValue);
            handleChange(selectedDate, timeInputValue);
            setSelectedCalendarDate(selectedDate);
            toggleCalendar(false);
          }}
        >
          <Trans>Confirm</Trans>
        </Button>
      </div>
    </DatePicker>
  );
};
